<template>
  <section class="account-profile account__body-inner">
    <p
      v-if="profileCompletionPercentage !== 100"
      class="account-profile__heading"
    >
      {{ $t('account.profile.completeProfile') }}
    </p>
    <HintBar
      v-if="isProfileUpdated"
      :hintData="hintProfileUpdated"
      class="account-profile__hintbar"
    />
    <HintBar
      v-if="showResetPasswordHint"
      :key="hintResetPassword.message"
      :hintData="hintResetPassword"
      class="account-profile__hintbar"
    />

    <form class="account-profile__form" @submit.prevent="saveProfile">
      <ul class="account-profile__list">
        <li class="account-profile__item">
          <FormGroup
            v-model="$v.form.firstName.$model.value"
            @input="$emit('fieldChanged', form.firstName)"
            :field="$v.form.firstName"
            :isValidationRun="isValidationRun"
          />
        </li>
        <li class="account-profile__item">
          <FormGroup
            v-model="$v.form.lastName.$model.value"
            @input="$emit('fieldChanged', form.lastName)"
            :field="$v.form.lastName"
            :isValidationRun="isValidationRun"
          />
        </li>
        <li class="account-profile__item">
          <FormGroup
            v-model="$v.form.company.$model.value"
            @input="$emit('fieldChanged', form.company)"
            :field="$v.form.company"
            :isValidationRun="isValidationRun"
          />
        </li>
        <li class="account-profile__item">
          <FormGroup
            v-model="$v.form.phone.$model.value"
            @input="$emit('fieldChanged', form.phone)"
            :field="$v.form.phone"
            :isValidationRun="isValidationRun"
          />
        </li>
        <li class="account-profile__item">
          <FormSelect
            v-model="form.productInterests.value"
            :field="form.productInterests"
            :isValidationRun="isValidationRun"
            @input="$emit('fieldChanged', form.productInterests)"
          />
        </li>
        <li class="account-profile__item">
          <FormSelect
            v-model="form.customerRole.value"
            @input="$emit('fieldChanged', form.customerRole)"
            :field="form.customerRole"
            :isValidationRun="isValidationRun"
          />
        </li>
        <li class="account-profile__item">
          <div class="account-profile__item-inner">
            <FormGroup v-model="form.email.value" :field="form.email" />
            <p class="account-profile__field-hint" v-if="!isEmployee">
              {{ $t('account.profile.changePasswordCopy') }}
              <a
                @click.prevent="doResetPassword"
                href="/"
                class="link"
              >{{ $t('account.profile.changePasswordLink') }}</a>
            </p>
          </div>
        </li>
        <li v-if="showFriendsAndFamilyCode" class="account-profile__item"></li>
        <li v-if="showFriendsAndFamilyCode" class="account-profile__item account-profile__item--vertical">
          <FormGroup
            v-model="form.friendsAndFamilyCode.value"
            :field="form.friendsAndFamilyCode"
            :class="{'form-group--error': showFriendsAndFamilyCodeError}"
          />
          <span
            v-if="showFriendsAndFamilyCodeError"
            v-html="form.friendsAndFamilyCode.errors.required"
            class="form-group__hint"
          ></span>
        </li>
      </ul>
      <button
        :disabled="isLoading"
        type="submit"
        class="button button--primary account-profile__btn-save"
      >
        {{ $t('common.save') }}
      </button>
    </form>

    <loader v-if="isLoading"/>
  </section>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import settings from '@/settings'
import { required, maxLength, numeric, minLength } from 'vuelidate/lib/validators'
import LoaderMixin from '@/mixins/LoaderMixin'
import FormValidationMixin from '@/mixins/FormValidationMixin'
import FormGroup from '@/components/FormGroup'
import FormSelect from '@/components/FormSelect'
import HintBar from '@/components/HintBar'

export default {
  name: 'AccountProfile',
  components: {
    FormGroup,
    FormSelect,
    HintBar
  },
  mixins: [LoaderMixin, FormValidationMixin],
  computed: {
    ...mapState('user', ['userData']),
    ...mapGetters('user', ['profileCompletionPercentage', 'isEmployee']),
    ...mapGetters('app', ['isUSSite']),
    showFriendsAndFamilyCode () {
      return !this.isEmployee && this.isUSSite
    }
  },
  methods: {
    ...mapActions('user', ['updateUserData', 'applyInvitationsCode']),
    ...mapActions('app', ['doResetPassword']),
    saveProfile () {
      this.withPresendRoutine(async () => {
        this.showFriendsAndFamilyCodeError = false
        this.isProfileUpdated = false
        const formData = {}
        this.pushFormToData(formData)
        delete formData.friendsAndFamilyCode

        this.isLoading = true
        if (this.showFriendsAndFamilyCode && !this.userData.friendsAndFamilyCode && this.form.friendsAndFamilyCode.value) {
          const isValid = await this.applyInvitationsCode(this.form.friendsAndFamilyCode.value)
          this.showFriendsAndFamilyCodeError = !isValid
          if (!isValid) {
            this.isLoading = false
            return
          }
          this.updateFriendsAndFamilyCodeField()
        }
        this.sendForm(this.updateUserData, formData)
          .then(() => {
            this.isProfileUpdated = true
          }).catch(() => {
            this.isValidationRun = true
          }).finally(() => {
            this.isLoading = false
          })
      })
    },
    updateFriendsAndFamilyCodeField () {
      if (!this.isEmployee && this.userData.friendsAndFamilyCode && this.isUSSite) {
        this.form.friendsAndFamilyCode.value = this.$t('account.profile.activatedViaCode', { code: this.userData.friendsAndFamilyCode })
        this.form.friendsAndFamilyCode.disabled = true
      }
    }
  },
  created () {
    this.pushDataToForm(this.userData)
    this.updateFriendsAndFamilyCodeField()
    const query = { ...this.$route.query }
    const isResetInitiated = !!query[settings.app.queryTokens.resetPasswordInitiatedToken]
    const isResetCanceled = !!query[settings.app.queryTokens.resetPasswordCanceledToken]

    if (isResetInitiated) {
      if (!isResetCanceled) this.showResetPasswordHint = true
      delete query[settings.app.queryTokens.resetPasswordInitiatedToken]
      delete query[settings.app.queryTokens.resetPasswordCanceledToken]
      this.$router.replace({ query })
    }
  },
  updated () {
    if (this.showResetPasswordHint) this.showResetPasswordHint = false
  },
  validations () {
    return {
      form: {
        firstName: {
          value: {
            required,
            maxLength: maxLength(35),
            server: this.server
          }
        },
        lastName: {
          value: {
            required,
            maxLength: maxLength(35),
            server: this.server
          }
        },
        company: {
          value: {
            maxLength: maxLength(35),
            server: this.server
          }
        },
        phone: {
          value: {
            numeric,
            maxLength: maxLength(10),
            minLength: this.form.phone.value ? minLength(10) : true,
            server: this.server
          }
        }
      }
    }
  },
  data () {
    return {
      isValidationRun: false,
      isProfileUpdated: false,
      showResetPasswordHint: false,
      showFriendsAndFamilyCodeError: false,
      hintResetPassword: {
        variant: 'success',
        iconColor: 'success',
        message: this.$t('account.profile.hintResetPassword')
      },
      form: {
        firstName: {
          placeholder: this.$t('account.profile.form.firstName.placeholder'),
          value: '',
          defaultValue: '',
          title: 'firstName',
          type: 'text',
          variant: 'shadow',
          maxLength: '35',
          errors: {
            required: this.$t('account.profile.form.firstName.requiredError'),
            minLength: this.$t('account.profile.form.firstName.minLengthError')
          }
        },
        lastName: {
          placeholder: this.$t('account.profile.form.lastName.placeholder'),
          value: '',
          defaultValue: '',
          title: 'lastName',
          type: 'text',
          variant: 'shadow',
          maxLength: '35',
          errors: {
            required: this.$t('account.profile.form.lastName.requiredError'),
            minLength: this.$t('account.profile.form.lastName.minLengthError')
          }
        },
        company: {
          placeholder: this.$t('account.profile.form.company.placeholder'),
          value: '',
          defaultValue: '',
          title: 'companyName',
          type: 'text',
          variant: 'shadow',
          maxLength: '35',
          errors: {
            maxLength: this.$t('account.profile.form.company.maxLengthError')
          }
        },
        phone: {
          inputType: 'the-mask',
          placeholder: this.$t('account.profile.form.phone.placeholder'),
          value: '',
          defaultValue: '',
          title: 'contactNumber',
          type: 'tel',
          mask: '###-###-####',
          variant: 'shadow',
          errors: {
            numeric: this.$t('account.profile.form.phone.numericError'),
            minLength: this.$t('account.profile.form.phone.numericError')
          }
        },
        productInterests: {
          type: 'select',
          options: [
            {
              name: ' ',
              code: ''
            },
            {
              name: this.$t('account.profile.form.productInterests.industrial'),
              code: 'industrial'
            },
            {
              name: this.$t('account.profile.form.productInterests.commercial'),
              code: 'commercial'
            },
            {
              name: this.$t('account.profile.form.productInterests.residential'),
              code: 'residential'
            }
          ],
          titleField: 'name',
          valueField: 'code',
          value: null,
          title: 'productInterests',
          variant: 'shadow',
          placeholder: this.$t('account.profile.form.productInterests.placeholder')
        },
        customerRole: {
          type: 'select',
          options: [
            {
              name: ' ',
              code: ''
            },
            {
              name: this.$t('account.profile.form.customerRole.contractor'),
              code: 'contractor'
            },
            {
              name: this.$t('account.profile.form.customerRole.engineer'),
              code: 'engineer'
            },
            {
              name: this.$t('account.profile.form.customerRole.homeowner'),
              code: 'homeowner'
            }
          ],
          titleField: 'name',
          valueField: 'code',
          value: null,
          title: 'customerRole',
          variant: 'shadow',
          placeholder: this.$t('account.profile.form.customerRole.placeholder')
        },
        email: {
          placeholder: this.$t('account.profile.form.email.placeholder'),
          value: '',
          defaultValue: '',
          title: 'uid',
          type: 'email',
          disabled: true,
          variant: 'shadow',
          maxLength: '255'
        },
        friendsAndFamilyCode: {
          placeholder: this.$t('account.profile.form.friendsAndFamilyCode.placeholder'),
          value: '',
          defaultValue: '',
          title: 'friendsAndFamilyCode',
          type: 'text',
          disabled: false,
          variant: 'shadow',
          maxLength: '255'
        }
      },
      hintProfileUpdated: {
        variant: 'success',
        iconColor: 'success',
        message: this.$t('account.profile.hintProfileUpdated')
      }
    }
  }
}
</script>
